/**
 * The standard wire envelope every m8t backend route returns.
 * Single source of truth shared between backend (apps/web) and CLI (apps/cli).
 */
export type ApiResponse<T> =
  | { ok: true; data: T }
  | {
      ok: false;
      error: {
        code: string;
        message: string;
        details?: unknown;
      };
    };

/**
 * Type-narrowing extract of the error variant. Useful when you have a known
 * failure envelope and want to dig into the error fields without re-narrowing.
 */
export type ApiErrorEnvelope = Extract<ApiResponse<unknown>, { ok: false }>["error"];

/**
 * Type guard for callers that received a parsed JSON of unknown shape.
 */
export function isApiResponse<T = unknown>(value: unknown): value is ApiResponse<T> {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  if (v.ok === true && "data" in v) return true;
  if (v.ok === false && typeof v.error === "object" && v.error !== null) {
    const e = v.error as Record<string, unknown>;
    return typeof e.code === "string" && typeof e.message === "string";
  }
  return false;
}
